import { apiClient } from './client';

export interface ReflectionCommit {
  commit_id: string;
  tenant_id: string;
  agent_type: string;
  user_id: string;
  run_id: string;
  parent_run_id: string | null;
  input_digest: string;
  status: 'pending' | 'processing' | 'committed' | 'failed';
  model_provenance: Record<string, unknown>;
  config_provenance: Record<string, unknown>;
  attempt: number;
  error_code: string | null;
  error_detail: Record<string, unknown> | null;
  committed_workspace_snapshot_id: string | null;
  adoption_status: 'not_applicable' | 'complete' | 'missing' | 'invalid';
  adopted_branch_ids: string[];
  adoption_error: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  committed_at: string | null;
}

export interface WorkspaceSnapshot {
  snapshot_id: string;
  tenant_id: string;
  agent_type: string;
  user_id: string;
  run_id: string;
  self_contract_revision: number;
  attended_state: unknown[];
  control_vars: Record<string, unknown>;
  token_count: number;
  item_count: number;
  expires_at: string;
  created_at: string;
}

export interface ExperienceEvent {
  event_id: string;
  tenant_id: string;
  agent_type: string;
  scope_kind: string;
  user_id: string | null;
  run_id: string;
  event_type: string;
  evidence_refs: Record<string, unknown>[];
  causal_parent_event_ids: string[];
  payload: Record<string, unknown>;
  occurred_at_utc: string;
  recorded_at_utc: string;
}

export interface BeliefRevision {
  belief_revision_id: string;
  belief_id: string;
  revision: number;
  supersedes_revision_id: string | null;
  statement: string;
  confidence: number;
  evidence_event_ids: string[];
  status: string;
  valid_until: string | null;
  created_at: string;
}

export interface CommitmentRevision {
  commitment_revision_id: string;
  commitment_id: string;
  revision: number;
  supersedes_revision_id: string | null;
  commitment: string;
  authorization_ref: string;
  status: string;
  temporal_workflow_id: string | null;
  fulfillment_event_ids: string[];
  due_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface RelationshipRevision {
  revision_id: string;
  relationship_id: string;
  revision: number;
  supersedes_revision_id: string | null;
  scope_kind: string;
  as_of_utc: string;
  state: Record<string, unknown>;
  memory_projection: unknown[];
  derived_from_event_ids: string[];
  created_at: string;
}

export interface MemoryProjection {
  projection_id: string;
  reflection_commit_id: string;
  source_event_id: string;
  status: string;
  attempt: number;
  mem0_memory_id: string | null;
  source_provenance: Record<string, unknown>;
  error_code: string | null;
  error_detail: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  projected_at: string | null;
}

export interface ContinuityRunList {
  items: ReflectionCommit[];
  total: number;
  offset: number;
  limit: number;
}

export interface ContinuityRunDetail {
  reflection: ReflectionCommit | null;
  workspace: WorkspaceSnapshot | null;
  workspace_timeline: WorkspaceSnapshot[];
  experiences: ExperienceEvent[];
  provenance_events: ExperienceEvent[];
  missing_provenance_event_ids: string[];
  provenance_complete: boolean;
  beliefs: BeliefRevision[];
  commitments: CommitmentRevision[];
  relationships: RelationshipRevision[];
  memory_projections: MemoryProjection[];
}

export interface ContinuityBaseline {
  generated_at: string;
  window_started_at: string;
  window_ended_at: string;
  qualification: 'short_window_trend_only';
  longitudinal_claim: false;
  reflection_status_counts: Record<string, number>;
  adoption_status_counts: Record<string, number>;
  projection_status_counts: Record<string, number>;
  reflection_sample_size: number;
  projection_sample_size: number;
  reflection_failed_rate: number;
  reflection_missing_rate: number;
  adoption_valid_rate: number;
  oldest_pending_age_seconds: number;
  average_commit_duration_seconds: number;
  average_projection_lag_seconds: number;
  oldest_projection_backlog_age_seconds: number;
  cross_user_isolation_violations: number;
}

export interface ContinuityFilters {
  tenantId: string;
  agentType?: string;
  userId?: string;
  runId?: string;
  status?: string;
  adoptionStatus?: string;
}

function params(filters: ContinuityFilters): Record<string, string> {
  return Object.fromEntries(
    Object.entries({
      tenant_id: filters.tenantId,
      agent_type: filters.agentType,
      user_id: filters.userId,
      run_id: filters.runId,
      status: filters.status,
      adoption_status: filters.adoptionStatus,
    }).filter((entry): entry is [string, string] => Boolean(entry[1])),
  );
}

function baselineParams(filters: ContinuityFilters): Record<string, string> {
  return Object.fromEntries(
    Object.entries({
      tenant_id: filters.tenantId,
      agent_type: filters.agentType,
      user_id: filters.userId,
    }).filter((entry): entry is [string, string] => Boolean(entry[1])),
  );
}

export interface QualificationMetric {
  key: string;
  value: number | null;
  numerator: number | null;
  denominator: number;
  eligible: number;
  inspected: number;
  unknown: number;
  excluded: number;
  status: 'no_samples' | 'unavailable' | 'partial' | 'measured' | 'violation';
  formula: string;
  counts: Record<string, number>;
  evidence: Record<string, unknown>[];
  coverage_note: string;
}

export interface QualificationReport {
  schema_version: 'continuity.qualification.v1';
  generated_at: string;
  scope: { tenant_id: string; agent_type: string; user_id: string; window_started_at: string; window_ended_at: string; sample_limit: number };
  metrics: QualificationMetric[];
  qualification: 'diagnostics_and_trends_only';
  runtime_isolation_probe: 'not_evaluated';
  owner_acceptance: 'not_evaluated';
}

export const continuityObservabilityApi = {
  async getQualification(filters: Required<Pick<ContinuityFilters, 'tenantId' | 'agentType' | 'userId'>>, window: { start: string; end: string }): Promise<QualificationReport> {
    const response = await apiClient.get<QualificationReport>('/continuity-observability/qualification', {
      params: { ...baselineParams(filters), window_started_at: window.start, window_ended_at: window.end, sample_limit: 32 },
    });
    return response.data;
  },
  async getAdminTenant(): Promise<string> {
    const response = await apiClient.get<{ tenant_id: string }>('/verify');
    return response.data.tenant_id;
  },

  async listRuns(filters: ContinuityFilters): Promise<ContinuityRunList> {
    const response = await apiClient.get<ContinuityRunList>('/continuity-observability/runs', {
      params: params(filters),
    });
    return response.data;
  },

  async getRun(filters: Required<Pick<ContinuityFilters, 'tenantId' | 'agentType' | 'userId' | 'runId'>>): Promise<ContinuityRunDetail> {
    const response = await apiClient.get<ContinuityRunDetail>(
      `/continuity-observability/runs/${encodeURIComponent(filters.runId)}`,
      { params: params(filters) },
    );
    return response.data;
  },

  async getBaseline(filters: ContinuityFilters, windowHours = 24): Promise<ContinuityBaseline> {
    const response = await apiClient.get<ContinuityBaseline>('/continuity-observability/baseline', {
      params: { ...baselineParams(filters), window_hours: windowHours },
    });
    return response.data;
  },
};
